import {
  useId,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { AlertIcon, CheckIcon, ClockIcon, GearIcon, PauseIcon, XIcon } from './icons.js';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'soft' | 'link';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-blue-700 text-white hover:bg-blue-800 disabled:bg-blue-400',
  secondary: 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-100',
  danger: 'border border-red-300 bg-white text-red-800 hover:bg-red-50',
  // Every standalone action looks like a button (owner's request, T-47); "link" is the soft one.
  soft: 'border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100',
  link: 'border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100',
};

/** Classes of a button, also for <a> and <Link> that navigate. */
export function buttonClass(variant: ButtonVariant = 'primary', size: 'md' | 'sm' = 'md') {
  const box = size === 'sm' ? 'min-h-9 px-3 py-1 text-sm' : 'min-h-11 px-4 py-2 text-base';
  return `inline-flex items-center justify-center gap-2 rounded-md font-semibold no-underline ${box} ${BUTTON_STYLES[variant]}`;
}

export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || busy}
      aria-busy={busy || undefined}
      className={`${buttonClass(variant, size)} ${className}`}
    >
      {children}
    </button>
  );
}

/** A link that moves to another page, drawn as a button (T-47). */
export function LinkButton({
  variant = 'soft',
  size = 'md',
  className = '',
  ...props
}: ComponentProps<typeof Link> & { variant?: ButtonVariant; size?: 'md' | 'sm' }) {
  return <Link {...props} className={`${buttonClass(variant, size)} ${className}`} />;
}

interface FieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  optional,
  id,
  ...props
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const { t } = useTranslation();
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy =
    [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="font-semibold">
        {label}
        {optional ? (
          <span className="font-normal text-slate-600"> ({t('app.optional')})</span>
        ) : null}
      </label>
      {hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-slate-700">
          {hint}
        </p>
      ) : null}
      <input
        id={fieldId}
        {...props}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`min-h-11 rounded-md border px-3 py-2 text-base ${error ? 'border-red-700' : 'border-slate-400'} bg-white`}
      />
      {error ? (
        <p
          id={`${fieldId}-error`}
          className="flex items-center gap-1 text-sm font-medium text-red-800"
        >
          <AlertIcon />
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  error,
  id,
  children,
  ...props
}: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="font-semibold">
        {label}
      </label>
      {hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-slate-700">
          {hint}
        </p>
      ) : null}
      <select
        id={fieldId}
        {...props}
        aria-describedby={hint ? `${fieldId}-hint` : undefined}
        className="min-h-11 rounded-md border border-slate-400 bg-white px-3 py-2 text-base"
      >
        {children}
      </select>
      {error ? <p className="text-sm font-medium text-red-800">{error}</p> : null}
    </div>
  );
}

export function Checkbox({
  label,
  error,
  id,
  ...props
}: { label: ReactNode; error?: string | undefined } & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-3">
        <input
          id={fieldId}
          type="checkbox"
          {...props}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          className="mt-1 h-5 w-5 shrink-0 accent-blue-700"
        />
        <label htmlFor={fieldId}>{label}</label>
      </div>
      {error ? (
        <p id={`${fieldId}-error`} className="text-sm font-medium text-red-800">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Message box; errors are announced at once (role=alert), success politely (role=status). */
export function Notice({
  kind,
  children,
}: {
  kind: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-red-300 bg-red-50 text-red-900',
    success: 'border-green-300 bg-green-50 text-green-900',
    info: 'border-blue-200 bg-blue-50 text-blue-950',
    warning: 'border-amber-300 bg-amber-50 text-amber-950',
  }[kind];
  const icon = kind === 'success' ? <CheckIcon /> : kind === 'info' ? <ClockIcon /> : <AlertIcon />;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md border px-4 py-3 ${styles}`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>{children}</div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6 ${className}`}
    >
      {children}
    </section>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <p role="status" className="py-8 text-center text-slate-700">
      {t('app.loading')}
    </p>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Notice kind="error">
      <p>{message}</p>
      <Button variant="link" onClick={onRetry}>
        {t('app.retry')}
      </Button>
    </Notice>
  );
}

export type MonitorState =
  | 'unknown'
  | 'up'
  | 'suspect'
  | 'down'
  | 'degraded'
  | 'paused'
  /** Not a monitor state: a bot protection hides the site until the customer allows us in. */
  | 'setup';

/** State with icon and text, never colour alone. */
export function StatusBadge({ state }: { state: MonitorState }) {
  const { t } = useTranslation();
  const look = {
    up: ['bg-green-100 text-green-900', <CheckIcon key="i" />],
    down: ['bg-red-100 text-red-900', <XIcon key="i" />],
    suspect: ['bg-amber-100 text-amber-950', <ClockIcon key="i" />],
    degraded: ['bg-amber-100 text-amber-950', <AlertIcon key="i" />],
    paused: ['bg-slate-200 text-slate-900', <PauseIcon key="i" />],
    unknown: ['bg-slate-100 text-slate-800', <ClockIcon key="i" />],
    setup: ['bg-violet-100 text-violet-900', <GearIcon key="i" />],
  }[state] as [string, ReactNode];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold ${look[0]}`}
    >
      {look[1]}
      {t(`status.${state}`)}
    </span>
  );
}

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation();
  const style = {
    critical: 'bg-red-100 text-red-900',
    high: 'bg-orange-100 text-orange-950',
    medium: 'bg-amber-100 text-amber-950',
    low: 'bg-slate-100 text-slate-900',
    info: 'bg-blue-50 text-blue-950',
  }[severity];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-sm font-semibold ${style}`}>
      {t(`severity.${severity}`)}
    </span>
  );
}
